import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { BaseView, BaseText, AppButton } from '@/components'
import { LabelAmount } from '@/components/LabelAmount'
import { getCoinGif } from '@/constants/coinGifs'
import useOffsets from '@/hooks/useOffsets'
import type { CryptoCode } from '@/constants/currencyCodes'

interface SuccessState {
  amount: string
  code: CryptoCode
  title: string
  description?: string
  relativeFiatFormatted?: string
  currencyLabel?: string
  doneRoute?: string
  navigationMethod?: 'dismiss' | 'replace'
}

export default function TransferOutSuccessScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const offsets = useOffsets()

  const state = location.state as SuccessState | null

  const handleDone = () => {
    if (state?.doneRoute) {
      if (state.navigationMethod === 'dismiss') {
        // For dismiss, we'll use replace as we don't have dismiss functionality in web
        navigate(state.doneRoute, { replace: true })
      } else {
        navigate(state.doneRoute, { replace: true })
      }
    } else {
      // Default navigation to savings page (similar to React Native)
      navigate('/savings', { replace: true })
    }
  }

  return (
    <BaseView
      flex={1}
      bgColor="background.screen"
      style={{
        height: '100vh',
        paddingTop: offsets.top,
      }}
    >
      <BaseView flex={1} px={12} className="flex flex-col h-full">
        {/* Central area - as in RN version */}
        <BaseView
          className="flex-1 flex flex-col justify-center items-center"
          gap={48}
        >
          {/* Animated currency icon */}
          {state?.code && (
            <BaseView center>
              <img
                src={getCoinGif(state.code)}
                alt={`${state.code} animation`}
                style={{
                  width: '180px',
                  height: '180px',
                  objectFit: 'contain',
                }}
              />
            </BaseView>
          )}

          {/* Content */}
          <BaseView gap={24} center>
            {/* Title */}
            <BaseText
              fontSize={24}
              fontFamily="medium"
              color="label.default"
              textAlign="center"
            >
              {t('translation:transferOut.youTransferred')}
            </BaseText>

            {/* Amount and fiat equivalent */}
            {state?.amount && state?.code && (
              <BaseView center gap={16}>
                <LabelAmount
                  amount={state.amount}
                  currencyCode={state.code}
                  currencyLabel={state.currencyLabel}
                  centralize
                />
                {state.relativeFiatFormatted && (
                  <BaseText
                    color="label.description"
                    textAlign="center"
                    fontSize={16}
                  >
                    ≈{state.relativeFiatFormatted}
                  </BaseText>
                )}
              </BaseView>
            )}

            {/* Description */}
            {state?.description && (
              <BaseText
                fontSize={20}
                color="label.description"
                textAlign="center"
              >
                {t(state.description)}
              </BaseText>
            )}
          </BaseView>
        </BaseView>

        {/* Done button */}
        <BaseView style={{ paddingBottom: offsets.bottom }}>
          <AppButton
            label={t('form:actions.done')}
            onPress={handleDone}
            type="secondaryGray"
          />
        </BaseView>
      </BaseView>
    </BaseView>
  )
}
